import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { BuildingOfficeIcon, CogIcon, MapPinIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';

interface HierarchyData {
  id: string;
  name: string;
  level_type: string;
  level: number;
  parent_id?: string;
  status: string;
  // When true, this node is the synthetic tenant root for its hierarchy.
  // Backend may expose this as is_tenant_root on hierarchy rows.
  is_tenant_root?: boolean;
  children?: HierarchyData[];
}

interface Tenant {
  id: string;
  name: string;
  domain?: string;
  status: string;
  tenant_type?: string;
  admin_emails?: string;
}

const TenantHierarchiesPage: React.FC = () => {
  const { t } = useLanguage();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [businessHierarchy, setBusinessHierarchy] = useState<HierarchyData[]>([]);
  const [functionalHierarchy, setFunctionalHierarchy] = useState<HierarchyData[]>([]);
  const [geographicHierarchy, setGeographicHierarchy] = useState<HierarchyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tenantTypeFilter, setTenantTypeFilter] = useState<string>('');

  useEffect(() => {
    loadTenants();
  }, [statusFilter, tenantTypeFilter]);

  useEffect(() => {
    if (selectedTenantId) {
      loadHierarchies(selectedTenantId);
    } else {
      // Clear hierarchies when no tenant is selected
      setBusinessHierarchy([]);
      setFunctionalHierarchy([]);
      setGeographicHierarchy([]);
      setSelectedTenant(null);
    }
  }, [selectedTenantId]);

  const loadTenants = async () => {
    try {
      setLoadingTenants(true);
      const params: any = {
        page: 1,
        limit: 1000 // Get all tenants
      };
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      if (tenantTypeFilter) {
        params.tenant_type = tenantTypeFilter;
      }
      
      const response = await api.get('/tenants', { params });
      
      if (response.data.success) {
        setTenants(response.data.data || []);
      }
    } catch (err) {
      setError(t('pages.tenantHierarchies.failedToLoadTenants') || 'Failed to load tenants');
      console.error('Error loading tenants:', err);
    } finally {
      setLoadingTenants(false);
    }
  };


  const loadHierarchies = async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Find the selected tenant for display
      const tenant = tenants.find(t => t.id === tenantId);
      setSelectedTenant(tenant || null);
      
      // Load all hierarchies in parallel using tenant_id query parameter
      const [businessRes, functionalRes, geographicRes] = await Promise.all([
        api.get('/hierarchy/business', { params: { tenant_id: tenantId } }),
        api.get('/hierarchy/functional', { params: { tenant_id: tenantId } }),
        api.get('/hierarchy/geographic', { params: { tenant_id: tenantId } })
      ]);

      setBusinessHierarchy(buildHierarchyTree(businessRes.data.data || businessRes.data));
      setFunctionalHierarchy(buildHierarchyTree(functionalRes.data.data || functionalRes.data));
      setGeographicHierarchy(buildHierarchyTree(geographicRes.data.data || geographicRes.data));

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || t('pages.tenantHierarchies.failedToLoad');
      setError(errorMessage);
      console.error('Error loading hierarchies:', err);
      
      // Clear hierarchies on error
      setBusinessHierarchy([]);
      setFunctionalHierarchy([]);
      setGeographicHierarchy([]);
    } finally {
      setLoading(false);
    }
  };

  const buildHierarchyTree = (data: any[]): HierarchyData[] => {
    if (!data || data.length === 0) return [];
    
    const map = new Map();
    const roots: HierarchyData[] = [];

    // Create map of all nodes
    data.forEach(item => {
      map.set(item.id, { ...item, children: [] });
    });

    // Build tree structure
    data.forEach(item => {
      const node = map.get(item.id);
      if (item.parent_id && map.has(item.parent_id)) {
        const parent = map.get(item.parent_id);
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const countNodes = (nodes: HierarchyData[]): number => {
    let count = nodes.length;
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        count += countNodes(node.children);
      }
    });
    return count;
  };

  const renderHierarchyNode = (node: HierarchyData, level: number = 0) => (
    <div key={node.id} className="ml-4">
      <div className={`flex items-center space-x-2 py-1 ${level > 0 ? 'border-l-2 border-[var(--color-border)] pl-3' : ''}`}>
        <div className={`w-2 h-2 rounded-full ${
          node.status === 'ACTIVE' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-secondary-text)] opacity-50'
        }`} />
        <span className="text-sm font-medium text-[var(--color-surface-text)]">
          {node.name}
        </span>
        <span className="text-xs text-[var(--color-secondary-text)] bg-[var(--color-secondary-bg)] px-2 py-1 rounded">
          {node.level_type}
        </span>
        {node.is_tenant_root && (
          <span className="text-xs font-semibold text-[var(--color-accent-text)] bg-[var(--color-accent-bg)]/10 border border-[var(--color-accent-bg)] px-2 py-0.5 rounded-full">
            {t('hierarchies.tenantRoot') || 'Tenant root'}
          </span>
        )}
        {node.level > 1 && (
          <span className="text-xs text-[var(--color-secondary-text)] opacity-75">
            {t('hierarchies.level') || 'Level'} {node.level}
          </span>
        )}
      </div>
      {node.children && node.children.length > 0 && (
        <div className="ml-4">
          {node.children.map(child => renderHierarchyNode(child, level + 1))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-surface-text)]">
          {t('pages.tenantHierarchies.title') || 'Tenant Hierarchies'}
        </h1>
        <p className="text-[var(--color-secondary-text)]">
          {t('pages.tenantHierarchies.description') || 'View hierarchies for any tenant'}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
        <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
          {t('pages.tenantHierarchies.filters') || 'Filters'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Account Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              {t('pages.tenantHierarchies.accountStatus') || 'Account Status'}
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
            >
              <option value="">{t('pages.tenantHierarchies.allStatuses') || 'All Statuses'}</option>
              <option value="ACTIVE">{t('pages.tenantHierarchies.active') || 'Active'}</option>
              <option value="INACTIVE">{t('pages.tenantHierarchies.inactive') || 'Inactive'}</option>
              <option value="SUSPENDED">{t('pages.tenantHierarchies.suspended') || 'Suspended'}</option>
            </select>
          </div>

          {/* Account Type Filter */}
          <div>
            <label htmlFor="tenant-type-filter" className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              {t('pages.tenantHierarchies.accountType') || 'Account Type'}
            </label>
            <select
              id="tenant-type-filter"
              value={tenantTypeFilter}
              onChange={(e) => setTenantTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
            >
              <option value="">{t('pages.tenantHierarchies.allTypes') || 'All Types'}</option>
              <option value="TENANT">{t('pages.tenantHierarchies.tenant') || 'Tenant'}</option>
              <option value="INDIVIDUAL">{t('pages.tenantHierarchies.individual') || 'Individual'}</option>
              <option value="CONSULTANT">{t('pages.tenantHierarchies.consultant') || 'Consultant'}</option>
              <option value="SUPER_TENANT">{t('pages.tenantHierarchies.superTenant') || 'Super Tenant'}</option>
              <option value="PORTCO">{t('pages.tenantHierarchies.portco') || 'Portfolio Company'}</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setStatusFilter('');
                setTenantTypeFilter('');
              }}
              className="w-full px-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-border)] transition-colors"
            >
              {t('pages.tenantHierarchies.clearFilters') || 'Clear Filters'}
            </button>
          </div>
        </div>
      </div>

      {/* Tenant Selection */}
      <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
        <label htmlFor="tenant-select" className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
          {t('pages.tenantHierarchies.selectTenant') || 'Select Tenant'}
        </label>
        <select
          id="tenant-select"
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          className="w-full px-4 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
          disabled={loadingTenants}
        >
          <option value="">
            {loadingTenants 
              ? (t('pages.tenantHierarchies.loadingTenants') || 'Loading tenants...')
              : (t('pages.tenantHierarchies.selectTenantPlaceholder') || '-- Select a tenant --')
            }
          </option>
          {tenants.map(tenant => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name} {tenant.domain ? `(${tenant.domain})` : ''} {tenant.admin_emails ? `- Admin: ${tenant.admin_emails}` : ''}
            </option>
          ))}
        </select>
        {tenants.length === 0 && !loadingTenants && (
          <p className="mt-2 text-sm text-[var(--color-secondary-text)]">
            {t('pages.tenantHierarchies.noTenantsMatchFilters') || 'No tenants match the selected filters'}
          </p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)] rounded-lg p-4">
          <p className="text-[var(--color-error)]">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-[var(--color-surface-text)]">
            {t('pages.tenantHierarchies.loadingHierarchies') || 'Loading hierarchies...'}
          </div>
        </div>
      )}

      {/* Hierarchies Display */}
      {!loading && selectedTenantId && selectedTenant && (
        <div>
          {/* Selected Tenant Info */}
          <div className="mb-4 p-4 bg-[var(--color-secondary-bg)] rounded-lg border border-[var(--color-border)]">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
              {selectedTenant.name}
            </h2>
            {selectedTenant.domain && (
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('pages.tenantHierarchies.domain') || 'Domain'}: {selectedTenant.domain}
              </p>
            )}
            {selectedTenant.tenant_type && (
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('pages.tenantHierarchies.accountType') || 'Account Type'}: {selectedTenant.tenant_type}
              </p>
            )}
            {selectedTenant.status && (
              <p className="text-sm text-[var(--color-secondary-text)]">
                {t('pages.tenantHierarchies.status') || 'Status'}: {selectedTenant.status}
              </p>
            )}
            {selectedTenant.admin_emails && (
              <p className="text-sm text-[var(--color-secondary-text)] mt-2">
                <span className="font-medium">{t('pages.tenantHierarchies.administrator') || 'Administrator'}:</span> {selectedTenant.admin_emails}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Business Hierarchy */}
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
              <div className="flex items-center space-x-2 mb-4">
                <BuildingOfficeIcon className="h-6 w-6 text-[var(--color-info)]" />
                <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                  {t('hierarchies.businessHierarchy') || 'Business Hierarchy'}
                </h2>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-[var(--color-surface-text)] mb-2">
                  {businessHierarchy.length} {t('hierarchies.levels') || 'levels'} • {countNodes(businessHierarchy)} {t('hierarchies.nodes') || 'nodes'}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {businessHierarchy.length > 0 ? (
                    businessHierarchy.map(node => renderHierarchyNode(node))
                  ) : (
                    <p className="text-sm text-[var(--color-secondary-text)] italic">
                      {t('pages.tenantHierarchies.noHierarchyData') || 'No business hierarchy data'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Functional Hierarchy */}
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
              <div className="flex items-center space-x-2 mb-4">
                <CogIcon className="h-6 w-6 text-[var(--color-info)]" />
                <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                  {t('hierarchies.functionalHierarchy') || 'Functional Hierarchy'}
                </h2>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-[var(--color-surface-text)] mb-2">
                  {functionalHierarchy.length} {t('hierarchies.levels') || 'levels'} • {countNodes(functionalHierarchy)} {t('hierarchies.nodes') || 'nodes'}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {functionalHierarchy.length > 0 ? (
                    functionalHierarchy.map(node => renderHierarchyNode(node))
                  ) : (
                    <p className="text-sm text-[var(--color-secondary-text)] italic">
                      {t('pages.tenantHierarchies.noHierarchyData') || 'No functional hierarchy data'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Geographic Hierarchy */}
            <div className="bg-[var(--color-surface-bg)] rounded-lg p-6 border border-[var(--color-border)]">
              <div className="flex items-center space-x-2 mb-4">
                <MapPinIcon className="h-6 w-6 text-[var(--color-info)]" />
                <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
                  {t('hierarchies.geographicHierarchy') || 'Geographic Hierarchy'}
                </h2>
              </div>
              
              <div className="mb-4">
                <div className="text-sm text-[var(--color-surface-text)] mb-2">
                  {geographicHierarchy.length} {t('hierarchies.levels') || 'levels'} • {countNodes(geographicHierarchy)} {t('hierarchies.nodes') || 'nodes'}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {geographicHierarchy.length > 0 ? (
                    geographicHierarchy.map(node => renderHierarchyNode(node))
                  ) : (
                    <p className="text-sm text-[var(--color-secondary-text)] italic">
                      {t('pages.tenantHierarchies.noHierarchyData') || 'No geographic hierarchy data'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Tenant Selected Message */}
      {!loading && !selectedTenantId && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-[var(--color-secondary-text)]">
              {t('pages.tenantHierarchies.selectTenantToView') || 'Please select a tenant to view their hierarchies'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantHierarchiesPage;

